import React, { useEffect, useState } from "react";
import {
  Chart,
  Series,
  ArgumentAxis,
  ValueAxis,
  Legend,
  Title,
  Tooltip,
  CommonSeriesSettings,
} from "devextreme-react/chart";
import { SelectBox } from "devextreme-react/select-box";
import "./KCharts.css";
import { baseUrl } from "../../hooks/useFetch";

/* ── types ─────────────────────────────────────────────────── */
interface ApiRecord {
  cob_date: string;
  legal_entity: string;
  measure: string;
  value: number | null;
}

interface KFactorDataPoint {
  period: string;
  [measure: string]: string | number;
}

interface TotalsDataPoint {
  period: string;
  total: number;
  funds: number;
  buffer: number;
}

/* ── constants ─────────────────────────────────────────────── */
const API_BASE = `${baseUrl}/compliance`;

const ENTITY_OPTIONS: { display: string; api: string }[] = [
  { display: "Combined",        api: "combined" },
  { display: "CFP Commodities", api: "cfp commodities" },
  { display: "CFP Energy",      api: "cfp energy" },
  { display: "CFP Trading",     api: "cfp trading" },
  { display: "CF Pathways",     api: "cfp pathway" },
  { display: "CFP Services",    api: "cfp services" },
];

const BREAKDOWN_MEASURES: { field: string; name: string; color?: string }[] = [
  { field: "K-DTF (cash trades)",  name: "K-DTF (cash trades)" },
  { field: "K-DTF (derivatives)",  name: "K-DTF (derivatives)" },
  { field: "K-NPR",               name: "K-NPR",              color: "#db34c5ff" },
  { field: "K-NPR FX",            name: "K-NPR FX",           color: "#44e751ff" },
  { field: "K-TCD",               name: "K-TCD",              color: "#edf033ff" },
  { field: "K-CON",               name: "K-CON",              color: "#d32a2aff" },
  { field: "K-COH (cash trades)", name: "K-COH (cash trades)" },
  { field: "K-COH (derivatives)", name: "K-COH (derivatives)" },
];

/* ── helpers ───────────────────────────────────────────────── */
function formatPeriod(isoDate: string): string {
  const d = new Date(isoDate);
  return d.toLocaleDateString("en-GB", { month: "short", day: "2-digit", year: "numeric" });
}

function pivotForBreakdown(records: ApiRecord[]): KFactorDataPoint[] {
  const byDate: Record<string, KFactorDataPoint> = {};
  for (const r of records) {
    const period = formatPeriod(r.cob_date);
    if (!byDate[period]) byDate[period] = { period };
    const measure = (r.measure || "").trim();
    if (BREAKDOWN_MEASURES.some(m => m.field.toLowerCase() === measure.toLowerCase())) {
      byDate[period][measure] = r.value ?? 0;
    }
  }
  return Object.values(byDate);
}

function pivotForTotals(records: ApiRecord[]): TotalsDataPoint[] {
  const byDate: Record<string, TotalsDataPoint> = {};
  for (const r of records) {
    const period = formatPeriod(r.cob_date);
    if (!byDate[period]) byDate[period] = { period, total: 0, funds: 0, buffer: 0 };
    const m = (r.measure || "").trim().toLowerCase();
    if (m.includes("total k-factor")) byDate[period].total = r.value ?? 0;
    else if (m.includes("own funds"))  byDate[period].funds = r.value ?? 0;
    else if (m.includes("buffer"))     byDate[period].buffer = r.value ?? 0;
  }
  return Object.values(byDate);
}

/* ── component ─────────────────────────────────────────────── */
const KCharts: React.FC = () => {
  const [entity, setEntity] = useState<string>("Combined");
  const [breakdownData, setBreakdownData] = useState<KFactorDataPoint[]>([]);
  const [totalsData, setTotalsData] = useState<TotalsDataPoint[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    const apiEntity = ENTITY_OPTIONS.find(e => e.display === entity)?.api || "combined";

    setLoading(true);
    (async () => {
      try {
        const url = `${API_BASE}/k_factors/history?legal_entity=${encodeURIComponent(apiEntity)}`;
        const res = await fetch(url, { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: { data: ApiRecord[] } = await res.json();

        setBreakdownData(pivotForBreakdown(json.data));
        setTotalsData(pivotForTotals(json.data));
      } catch (e: any) {
        if (e?.name !== "AbortError") console.error("KCharts fetch error:", e);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [entity]);

  const handleLegendClick = (e: any) => {
    if (e.component) {
      const series = e.component.getAllSeries().find((s: any) => s.name === e.target.name);
      if (series) {
        series.isVisible() ? series.hide() : series.show();
      }
    }
  };

  return (
    <div className="charts-dashboard">
      <div className="charts-header">
        <SelectBox
          items={ENTITY_OPTIONS.map(e => e.display)}
          value={entity}
          onValueChanged={(e: any) => setEntity(e.value)}
          className="entity-select"
        />
        {loading && <span style={{ color: "#50baff", marginLeft: 12 }}>Loading...</span>}
      </div>

      <div className="charts-container">
        <Chart id="kfactor-breakdown" dataSource={breakdownData} onLegendClick={handleLegendClick}>
          <Title text={`K-Factor Breakdown — ${entity}`} font={{ size: 18, weight: 700, color: "#ffffff" }} />
          <ArgumentAxis argumentType="string" />
          <ValueAxis />
          <Tooltip enabled={true} format="largeNumber" zIndex={2147483000} />
          <Legend verticalAlignment="bottom" horizontalAlignment="center" />
          <CommonSeriesSettings type="stackedbar" />

          {BREAKDOWN_MEASURES.map(m => (
            <Series
              key={m.field}
              valueField={m.field}
              argumentField="period"
              name={m.name}
              color={m.color}
            />
          ))}
        </Chart>

        <Chart id="totals-chart" dataSource={totalsData} onLegendClick={handleLegendClick}>
          <Title text={`Totals vs Own Funds vs Buffer — ${entity}`} font={{ size: 18, weight: 700, color: "#ffffff" }} />
          <ArgumentAxis argumentType="string" />
          <ValueAxis />
          <Tooltip enabled={true} format="largeNumber" zIndex={2147483000} />
          <Legend verticalAlignment="bottom" horizontalAlignment="center" />

          <Series valueField="total" argumentField="period" name="Total K-Factor" type="bar" color="#4facfe" />
          <Series valueField="funds" argumentField="period" name="Own Funds" type="bar" color="#22d3ee" />
          <Series valueField="buffer" argumentField="period" name="Buffer" type="bar" color="#ff6b6b" />
        </Chart>
      </div>
    </div>
  );
};

export default KCharts;